// Форматирование для интерфейса (чистые функции, тестируются без DOM).

export function plural(n: number, forms: [string, string, string]): string {
  const abs = Math.abs(n) % 100;
  const last = abs % 10;
  if (abs > 10 && abs < 20) return forms[2];
  if (last > 1 && last < 5) return forms[1];
  if (last === 1) return forms[0];
  return forms[2];
}

export const seatsLabel = (n: number): string => `${n} ${plural(n, ['место', 'места', 'мест'])}`;

export function formatWhen(iso: string, timeZone?: string): string {
  const opts: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    ...(timeZone ? { timeZone } : {}),
  };
  return new Intl.DateTimeFormat('ru-RU', opts).format(new Date(iso));
}

/** Обозначение игры вместо названия: дата и время, затем название места. */
export function gameLabel(g: { startsAt: string; venue: string }, timeZone?: string): string {
  return `${formatWhen(g.startsAt, timeZone)} · ${g.venue}`;
}

/** Места, в которых есть игры из списка, для фильтра на главной: без повторов, по алфавиту. */
export function placeOptions(
  games: readonly { placeId: number; venue: string }[],
): { id: number; name: string }[] {
  const byId = new Map<number, string>();
  for (const g of games) byId.set(g.placeId, g.venue);
  return [...byId]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'ru') || a.id - b.id);
}

/** Значение для <input type="datetime-local"> из ISO (в локальном времени). */
export function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** ISO из значения <input type="datetime-local"> (локальное время браузера). Пустая строка — если дата неверна. */
export function fromLocalInput(value: string): string {
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString();
}

/**
 * Дата и время нового сбора при копировании: через неделю после копируемого, в тот же день недели и час.
 * Если и это уже прошло (копируют давний сбор), берётся ближайшая такая же дата в будущем.
 */
export function nextWeekOf(iso: string, now: Date): string {
  const d = new Date(iso);
  do {
    d.setDate(d.getDate() + 7);
  } while (d.getTime() <= now.getTime());
  return toLocalInput(d.toISOString());
}

/** Страница пользователя ВКонтакте; у тестовых игроков (vkId ≤ 0) страницы нет — null. */
export function profileUrl(vkId: number): string | null {
  return Number.isInteger(vkId) && vkId > 0 ? `https://vk.com/id${vkId}` : null;
}

/** Открыть страницу пользователя из меню действий (в новой вкладке; приложение VK открывает профиль у себя). */
export function openProfile(vkId: number): void {
  const url = profileUrl(vkId);
  if (url) window.open(url, '_blank', 'noopener,noreferrer');
}

export function fullName(u: { firstName: string; lastName: string }): string {
  return `${u.firstName} ${u.lastName}`.trim();
}

/** Имя для места записи в командах: само место — «Иван Петров», его друзья — «Иван Петров +1», «+2». */
export function seatName(u: { firstName: string; lastName: string }, seat: number): string {
  return seat <= 1 ? fullName(u) : `${fullName(u)} +${seat - 1}`;
}

/** «2 встречи»: сколько раз каждая пара команд играет между собой. */
export function meetingsLabel(n: number): string {
  return `${n} ${plural(n, ['встреча', 'встречи', 'встреч'])}`;
}

/**
 * Показывать ли сбор в списке на главной при живом обновлении — те же правила, что у сервера (GET /games):
 * прошедшие — только с фильтром showPast; отменённые (свои и чужие) — только с фильтром showCancelled;
 * арена — по фильтру placeId.
 */
export function visibleInList(
  game: { status: 'active' | 'cancelled'; isFinished: boolean; placeId: number },
  filters: { placeId: number | null; showPast: boolean; showCancelled: boolean },
): boolean {
  if (game.isFinished && !filters.showPast) return false;
  if (filters.placeId !== null && game.placeId !== filters.placeId) return false;
  return game.status === 'active' || filters.showCancelled;
}

/** Подпись состояния заполненности: «занято 18/24, в очереди 3». */
/** Формат игры для экрана игры: «до 4 команд по 6 игроков». */
export function teamsLabel(teamsMax: number, playersPerTeam: number): string {
  return `до ${teamsMax} ${plural(teamsMax, ['команды', 'команд', 'команд'])} по ${playersPerTeam} ${plural(playersPerTeam, ['игроку', 'игрока', 'игроков'])}`;
}

export function fillLabel(taken: number, capacity: number, queued: number): string {
  const base = `занято ${taken}/${capacity}`;
  return queued > 0 ? `${base}, в очереди ${queued}` : base;
}

/** Результат записи для тоста: «Вы записаны: 2 места в составе, 1 место в очереди». */
export function signupResultText(e: { mainSeats: number; queueSeats: number }): string {
  const parts: string[] = [];
  if (e.mainSeats > 0) parts.push(`${seatsLabel(e.mainSeats)} в составе`);
  if (e.queueSeats > 0) parts.push(`${seatsLabel(e.queueSeats)} в очереди`);
  return parts.length > 0 ? `Вы записаны: ${parts.join(', ')}` : 'Запись обновлена';
}

/** Начало новой игры по умолчанию: завтра в 21:00 по локальному времени (формат datetime-local). */
export function defaultStartsAt(now: Date): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 21, 0, 0, 0);
  return toLocalInput(d.toISOString());
}

/**
 * Включены ли у пользователя уведомления приложения: параметр запуска `vk_are_notifications_enabled`
 * (подписан VK). Вне VK или без параметра возвращает null.
 */
export function notificationsEnabledFromSearch(search: string): boolean | null {
  const v = new URLSearchParams(search).get('vk_are_notifications_enabled');
  if (v === '1') return true;
  if (v === '0') return false;
  return null;
}

/** Ссылка на игру внутри VK: https://vk.com/app<ID>#/game/<id> */
export function gameLink(appId: number, gameId: number): string {
  return `https://vk.com/app${appId}#/game/${gameId}`;
}

export type Route =
  | { name: 'list' }
  | { name: 'filters' }
  | { name: 'my' }
  | { name: 'create' }
  | { name: 'game'; id: number }
  | { name: 'edit'; id: number };

/** Разбор hash: `#/`, `#/filters`, `#/my`, `#/create`, `#/game/12`, `#/game/12/edit` (допускается и `#game/12`). */
export function parseRoute(hash: string): Route {
  const path = hash.replace(/^#\/?/, '');
  const parts = path.split('/').filter(Boolean);
  if (parts[0] === 'filters') return { name: 'filters' };
  if (parts[0] === 'my') return { name: 'my' };
  if (parts[0] === 'create') return { name: 'create' };
  if (parts[0] === 'game' && /^\d+$/.test(parts[1] ?? '')) {
    const id = Number(parts[1]);
    return parts[2] === 'edit' ? { name: 'edit', id } : { name: 'game', id };
  }
  return { name: 'list' };
}

export function routeToHash(r: Route): string {
  switch (r.name) {
    case 'list':
      return '#/';
    case 'filters':
      return '#/filters';
    case 'my':
      return '#/my';
    case 'create':
      return '#/create';
    case 'game':
      return `#/game/${r.id}`;
    case 'edit':
      return `#/game/${r.id}/edit`;
  }
}

/** Кусок текста; у номера телефона — phone в виде +7XXXXXXXXXX для копирования. */
export interface TextPart {
  text: string;
  phone?: string;
}

// +7 / 8 и 10 цифр, либо 10 цифр с 9 в начале; между группами пробелы, дефисы и скобки
const PHONE_RE = /(?<![\d+])(?:(?:\+7|8)[\s\-()]*\d|\(?9)\d{2}\)?[\s\-)]*\d{3}[\s-]*\d{2}[\s-]*\d{2}(?!\d)/g;

/** Делит текст на куски, выделяя российские номера телефона (например, в реквизитах для предоплаты). */
export function splitPhones(text: string): TextPart[] {
  const parts: TextPart[] = [];
  let last = 0;
  for (const m of text.matchAll(PHONE_RE)) {
    const digits = m[0].replace(/\D/g, '');
    const ten = digits.length === 11 ? digits.slice(1) : digits;
    if (ten.length !== 10) continue;
    if (m.index > last) parts.push({ text: text.slice(last, m.index) });
    parts.push({ text: m[0], phone: `+7${ten}` });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}

export type Appearance = 'light' | 'dark';

/**
 * Тема из данных VK (VKWebAppGetConfig / VKWebAppUpdateConfig): поле appearance, а если его нет — по названию схемы
 * (например, vkcom_dark, space_gray — тёмные). null — тему определить нельзя.
 */
export function appearanceFromVkConfig(data: unknown): Appearance | null {
  if (typeof data !== 'object' || data === null) return null;
  const d = data as { appearance?: unknown; scheme?: unknown };
  if (d.appearance === 'light' || d.appearance === 'dark') return d.appearance;
  if (typeof d.scheme === 'string') {
    if (/dark|space_gray/.test(d.scheme)) return 'dark';
    if (/light/.test(d.scheme)) return 'light';
  }
  return null;
}
